import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, TextField, MenuItem, Chip, Button, CircularProgress,
  IconButton, Menu, Snackbar, Alert, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PageContainer from '../../components/common/PageContainer';
import PageHeader from '../../components/common/PageHeader';
import PpLogo from '../../components/common/PpLogo';
import { HasPermission } from '../../functions/auth';
import { PERMISSIONS } from '../../constants/permissions';
import { useStateValue } from '../../context/StateValueContext';
import { hasPermission } from '../../functions/permissionUtils';
import { useApi } from '../../hooks/useApi';
import { useBrandId } from '../../hooks/useBrandId';
import { useCompanyId } from '../../hooks/useCompanyId';
import { usePpAccounts } from '../../hooks/usePpAccounts';
import { formatPpAccountLabel } from '../../utils/ppAccountLabel';
import { filterPanelSx } from '../../components/common/filterStyles';
import type { Enrolment, EnrolmentListResponse, EnrolmentStatus } from '../../types/alertEnrolment';
import { SINGLE_BRAND_PLATFORMS, NETWORK_COLUMNS, STATUS_COLORS, STATUS_OPTIONS, STATUS_OPTIONS_TRANSFER } from './enrolment/enrolmentConstants';
import EnrolmentFormDialog from './enrolment/EnrolmentFormDialog';
import EnrolmentHistoryDialog from './enrolment/EnrolmentHistoryDialog';
import EditNoteDialog from './enrolment/EditNoteDialog';
import DeleteEnrolmentDialog from './enrolment/DeleteEnrolmentDialog';

type SortBy = 'created_at' | 'pp_account_name' | 'pp_type';

const YES_NO_OPTIONS = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
const yesNoColor = (v: string) => (v === 'yes' ? { main: '#15803d', bg: '#e3f8ea' } : { main: '#6b7280', bg: '#f1f2f4' });
// Compact chip labels (the dropdown menu still shows the full STATUS_OPTIONS labels).
const STATUS_SHORT: Record<EnrolmentStatus, string> = { not_submitted: 'Not sub', submitted: 'Submitted', enrolled: 'Enrolled', transfer_pending: 'T Pending', transfer_in_progress: 'T progress' };

// Mirrors the server's case-insensitive substring match so the user can see WHY a row matched.
function highlightMatch(text: string, term: string): ReactNode {
  if (!term) return text;
  const lower = text.toLowerCase();
  const needle = term.toLowerCase();
  const parts: ReactNode[] = [];
  let i = 0;
  for (let idx = lower.indexOf(needle); idx !== -1; idx = lower.indexOf(needle, i)) {
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(<Box key={idx} component="mark" sx={{ bgcolor: '#ffd43b', borderRadius: '3px', px: 0.25, fontWeight: 700 }}>{text.slice(idx, idx + needle.length)}</Box>);
    i = idx + needle.length;
  }
  if (!parts.length) return text;
  if (i < text.length) parts.push(text.slice(i));
  return parts;
}

// A provider account is not the thing that settles: one Adyen account spans many merchant
// accounts, one CoC account many gateways. The badge names the sub-account the enrolment actually
// settles through - an Adyen merchant code, or a gateway's title falling back to its biller id.
function MappingBadge({ label }: { label: string }) {
  return (
    <Box component="span" sx={{
      fontSize: 11, fontWeight: 600, px: 0.6, py: 0.1, borderRadius: '6px', border: '1px solid',
      borderColor: '#e2e8f0', bgcolor: '#f8fafc', color: 'text.secondary', lineHeight: 1.5, whiteSpace: 'nowrap',
    }}>
      {label}
    </Box>
  );
}

// Keeps the 20px logo column even for a platform with no logo of its own ("Other" free text), so
// rows line up without a placeholder character standing in for the missing image.
function LogoSlot({ ppType }: { ppType: string | null }) {
  return (
    <Box sx={{ width: 20, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
      {ppType && <PpLogo ppType={ppType} fallback="" />}
    </Box>
  );
}

// Per-network verification badge: green + ✓ once verified, muted outline while awaiting the first alert/notice.
function VerificationBadge({ label, on }: { label: string; on: boolean }) {
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 10, fontWeight: 600,
      px: 0.6, py: 0.1, borderRadius: '6px', border: '1px solid', lineHeight: 1.5, whiteSpace: 'nowrap',
      ...(on ? { bgcolor: '#eef9f1', borderColor: '#cfeeda', color: '#15803d' } : { bgcolor: '#fff', borderColor: '#e2e8f0', color: '#94a3b8' }),
    }}>
      {on && <Box component="span" sx={{ fontSize: 8 }}>✓</Box>}{label}
    </Box>
  );
}

// A rounded, colored status pill that opens a menu on click — matches the app's Chip styling
// and keeps a fixed width so the table doesn't reflow when the status label length changes.
function StatusChipSelect({ value, options, colorFor, onChange, width, shortLabel, readOnly }: {
  value: string;
  options: { value: string; label: string }[];
  colorFor: (value: string) => { main: string; bg: string };
  onChange: (value: string) => void;
  width: number;
  shortLabel?: (value: string) => string;
  readOnly?: boolean;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const label = shortLabel ? shortLabel(value) : (options.find((o) => o.value === value)?.label ?? value);
  const c = colorFor(value) ?? { main: '#6b7280', bg: '#f1f2f4' };
  return (
    <>
      <Chip
        size="small"
        onClick={readOnly ? undefined : (ev) => setAnchor(ev.currentTarget)}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
            {label}
            {!readOnly && <ArrowDropDownIcon sx={{ fontSize: 14, mr: -0.5, ml: -0.25, opacity: 0.6 }} />}
          </Box>
        }
        sx={{
          width, height: 22, borderRadius: '999px', bgcolor: c.bg, color: c.main, fontWeight: 600,
          cursor: readOnly ? 'default' : 'pointer',
          '& .MuiChip-label': { px: 0.75, width: '100%' },
        }}
      />
      {!readOnly && (
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          {options.map((o) => (
            <MenuItem key={o.value} selected={o.value === value} onClick={() => { onChange(o.value); setAnchor(null); }}>
              {o.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

function NoteCell({ note }: { note: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [note]);
  const text = (
    <Box ref={ref} sx={{
      width: 260, fontSize: 13, lineHeight: 1.45,
      display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>{note}</Box>
  );
  if (!truncated) return text;
  return (
    <Tooltip arrow placement="top" title={note}
      slotProps={{ tooltip: { sx: { fontSize: 14, lineHeight: 1.6, maxWidth: 400, p: 1.5, whiteSpace: 'pre-wrap' } } }}>
      {text}
    </Tooltip>
  );
}

// One cell of the 3x3 status grid: the network/Dash label (+ optional info) and its status control.
function NetworkGridItem({ label, info, children }: { label: string; info?: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, minWidth: 0 }}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {label}
        {info && (
          <Tooltip arrow title={info}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  );
}

export default function AlertsEnrolment({ variant = 'admin' }: { variant?: 'admin' | 'client' }) {
  const isClient = variant === 'client';
  const apiBase = isClient ? '/api/client-enrolments' : '/api/alert-enrolments';
  const [{ user }] = useStateValue();
  const canWrite = isClient || hasPermission(user, PERMISSIONS.ENROLMENT_WRITE_ALL);
  // Separate instances so `loading` tracks the list fetch only, not the note PATCH.
  const { get, loading } = useApi();
  const { patch } = useApi();
  // Reuse the top-level company selection (URL ?companyId; null = All Companies).
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  // Existing accounts store only (pp_type, pp_account_id); resolve the display name live.
  const { ppAccounts } = usePpAccounts();
  const accountNameByKey = useMemo(() => new Map(ppAccounts.map((a) => [`${a.ppType}:${a.ppAccountId}`, formatPpAccountLabel(a)])), [ppAccounts]);
  // A slot names one of the company's accounts, or free text typed under the form's "Other"
  // option. Free text is labelled as such, so a name nobody can look up does not read like an
  // account that exists.
  const slotLabel = (type: string | null, id: number | null, freeText: string | null) => {
    if (type != null && id != null) return accountNameByKey.get(`${type}:${id}`) ?? `Account #${id}`;
    const text = freeText?.trim();
    return text ? `Other (${text})` : '';
  };
  const accountBrandByKey = useMemo(() => new Map(
    ppAccounts
      .filter((a) => (SINGLE_BRAND_PLATFORMS as readonly string[]).includes(a.ppType))
      .map((a) => [`${a.ppType}:${a.ppAccountId}`, a.brandName]),
  ), [ppAccounts]);
  const enrolmentBrandName = (e: Enrolment): string | null =>
    e.brandName
    ?? (e.ppAccountId != null ? accountBrandByKey.get(`${e.ppType}:${e.ppAccountId}`) : undefined)
    ?? (e.crmType != null && e.crmAccountId != null ? accountBrandByKey.get(`${e.crmType}:${e.crmAccountId}`) : undefined)
    ?? null;

  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const [rows, setRows] = useState<Enrolment[]>([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialSearch);
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<Enrolment | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<Enrolment | null>(null);
  const [historyRow, setHistoryRow] = useState<Enrolment | null>(null);
  const [noteRow, setNoteRow] = useState<Enrolment | null>(null);
  const [deleteRow, setDeleteRow] = useState<Enrolment | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const showError = (message: string) => setSnackbar({ open: true, message });

  const fetchEnrolments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (!isClient && companyId != null) params.set('companyId', String(companyId));
      if (brandId != null) params.set('brandId', String(brandId));
      if (debouncedSearch) params.set('search', debouncedSearch);
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      params.set('page', String(page + 1));
      params.set('pageSize', String(pageSize));
      const data = await get<EnrolmentListResponse>(`${apiBase}?${params.toString()}`);
      setRows(data.enrolments);
      setTotal(data.total);
    } catch (e: any) {
      showError(e?.message ?? 'Failed to load enrolments');
    }
  }, [get, isClient, apiBase, companyId, brandId, debouncedSearch, sortBy, sortOrder, page, pageSize]);

  useEffect(() => { fetchEnrolments(); }, [fetchEnrolments]);
  // Reset to the first page when the top-level company or brand changes.
  useEffect(() => { setPage(0); }, [companyId, brandId]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL whenever search changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
    } else {
      params.delete('search');
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSort = (col: SortBy) => {
    if (sortBy === col) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortOrder('asc'); }
    setPage(0);
  };

  const patchCell = async (id: number, body: Record<string, unknown>) => {
    try {
      const updated = await patch<Enrolment>(`/api/alert-enrolments/${id}`, body);
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (e: any) {
      showError(e?.message ?? 'Failed to update enrolment');
    }
  };
  const upsertRow = (u: Enrolment) => setRows((prev) => prev.map((r) => (r.id === u.id ? u : r)));

  const openMenu = (ev: React.MouseEvent<HTMLElement>, row: Enrolment) => { setMenuAnchor(ev.currentTarget); setMenuRow(row); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const descriptorChips = (list: Enrolment['descriptors']) =>
    list.map((d) => (
      <Chip key={d.id} label={highlightMatch(d.descriptor, debouncedSearch)} size="small" sx={{ m: 0.25 }} />
    ));

  return (
    <PageContainer>
      <PageHeader
        title="Enrolment Management"
        action={
          <HasPermission permission={[PERMISSIONS.ENROLMENT_WRITE_ALL, PERMISSIONS.ENROLMENT_OWN]}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditRow(null); setFormOpen(true); }}>
              New Enrolment
            </Button>
          </HasPermission>
        }
      />

      <Box className="mb-6" sx={filterPanelSx}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
          <TextField size="small" label="Search" placeholder="Account, CRM, descriptor, BIN, CAID, MCC" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} sx={{ minWidth: 260 }} />
        </Box>
      </Box>

      {!isClient && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5, fontSize: 12, color: 'text.secondary' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>Verification:</Box>
          <VerificationBadge label="Ethoca" on />
          <Box component="span">verified</Box>
          <VerificationBadge label="Ethoca" on={false} />
          <Box component="span">awaiting first event</Box>
        </Box>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: isClient ? 900 : 1340 }} size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'grey.50', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ minWidth: 220 }} sortDirection={sortBy === 'pp_account_name' ? sortOrder : false}>
                  <TableSortLabel active={sortBy === 'pp_account_name'} direction={sortBy === 'pp_account_name' ? sortOrder : 'asc'} onClick={() => handleSort('pp_account_name')}>
                    PP Account
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>CRM</TableCell>
                <TableCell>Descriptors</TableCell>
                <TableCell>Historic</TableCell>
                <TableCell>BIN / CAID / MCC</TableCell>
                {!isClient && <TableCell>Enrolment & Access</TableCell>}
                {!isClient && <TableCell sx={{ width: 260 }}>Notes</TableCell>}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => {
                const crmLabel = slotLabel(e.crmType, e.crmAccountId, e.crm);
                const brandName = enrolmentBrandName(e);
                const brandLine = !isClient && brandName ? brandName : null;
                return (
                <TableRow key={e.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LogoSlot ppType={e.ppType} />
                      <Box sx={{ minWidth: 0 }}>
                        <Box>{highlightMatch(slotLabel(e.ppType, e.ppAccountId, e.ppAccountName), debouncedSearch)}</Box>
                        {/* One Adyen account spans many merchant accounts, so the merchant the
                            enrolment settles through belongs with the account, not apart from it. */}
                        {e.adyenMerchantAccountCode && (
                          <Box sx={{ mt: 0.5 }}><MappingBadge label={e.adyenMerchantAccountCode} /></Box>
                        )}
                        {brandLine && (
                          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{brandLine}</Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {/* Its own column, not a second line under the processor: the two account slots
                      are independent, so an enrolment naming no CRM simply leaves this cell empty. */}
                  <TableCell>
                    {crmLabel && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Free text names no account, so it carries no platform logo. */}
                        <LogoSlot ppType={e.crmType} />
                        <Box>{highlightMatch(crmLabel, debouncedSearch)}</Box>
                      </Box>
                    )}
                    {/* A CoC gateway is a MID of its own and an enrolment settles through several,
                        so they wrap under the account rather than sitting in a column apart. */}
                    {e.checkoutChampGateways.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {e.checkoutChampGateways.map((g) => (
                          <MappingBadge key={g.billerId} label={g.title ?? String(g.billerId)} />
                        ))}
                      </Box>
                    )}
                    {e.crmConnectorGateways.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {e.crmConnectorGateways.map((g) => (
                          <MappingBadge key={g.id} label={g.name ?? g.id} />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {e.descriptors.map((d) => (
                      <Box key={d.id} sx={{ mb: 0.75 }}>
                        <Chip label={highlightMatch(d.descriptor, debouncedSearch)} size="small" />
                        {!isClient && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            <VerificationBadge label="Ethoca" on={d.ethocaVerified} />
                            <VerificationBadge label="CDRN" on={d.cdrnVerified} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell>{descriptorChips(e.historicDescriptors)}</TableCell>
                  <TableCell>
                    {e.combinations.map((c) => (
                      <Box key={c.id} sx={{ mb: 0.75 }}>
                        <Box sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {highlightMatch(c.bin, debouncedSearch)} · {highlightMatch(c.caid, debouncedSearch)}{c.mcc ? <> · {highlightMatch(c.mcc, debouncedSearch)}</> : ''}
                        </Box>
                        {!isClient && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            <VerificationBadge label="RDR" on={c.rdrVerified} />
                            <VerificationBadge label="OI/CE" on={c.oiceVerified} />
                            <VerificationBadge label="TC40" on={c.tc40Verified} />
                            <VerificationBadge label="TC15" on={c.tc15Verified} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </TableCell>
                  {!isClient && (
                    <>
                      <TableCell sx={{ minWidth: 470 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', columnGap: 2, rowGap: 0.75 }}>
                          {NETWORK_COLUMNS.map((n) => (
                            <NetworkGridItem key={n.network} label={n.label}>
                              <StatusChipSelect value={e[n.field] as EnrolmentStatus} options={n.transferable ? STATUS_OPTIONS_TRANSFER : STATUS_OPTIONS} width={94}
                                shortLabel={(v) => STATUS_SHORT[v as EnrolmentStatus]}
                                colorFor={(v) => STATUS_COLORS[v as EnrolmentStatus]}
                                readOnly={!canWrite}
                                onChange={(v) => patchCell(e.id, { [n.field]: v })} />
                            </NetworkGridItem>
                          ))}
                          <NetworkGridItem label="Dash" info="Indicates whether the client has granted us dashboard access for this PP account / CRM.">
                            <StatusChipSelect value={e.dashboardAccess ? 'yes' : 'no'} options={YES_NO_OPTIONS} width={54}
                              colorFor={yesNoColor}
                              readOnly={!canWrite}
                              onChange={(v) => patchCell(e.id, { dashboardAccess: v === 'yes' })} />
                          </NetworkGridItem>
                          <NetworkGridItem label="Representment" info="Whether we represent (fight) chargebacks for this PP account / CRM.">
                            <StatusChipSelect value={e.representment ? 'yes' : 'no'} options={YES_NO_OPTIONS} width={54}
                              colorFor={yesNoColor}
                              readOnly={!canWrite}
                              onChange={(v) => patchCell(e.id, { representment: v === 'yes' })} />
                          </NetworkGridItem>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: 260 }}>
                        {e.note ? <NoteCell note={e.note} /> : null}
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center">
                    <IconButton size="small" onClick={(ev) => openMenu(ev, e)}><MoreVertIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={isClient ? 6 : 8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No enrolments</TableCell></TableRow>
              )}
              {loading && (
                <TableRow><TableCell colSpan={isClient ? 6 : 8} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(ev) => { setPageSize(Number(ev.target.value)); setPage(0); }} />
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {canWrite && <MenuItem onClick={() => { setEditRow(menuRow); setFormOpen(true); closeMenu(); }}>Edit</MenuItem>}
        {!isClient && canWrite && <MenuItem onClick={() => { setNoteRow(menuRow); closeMenu(); }}>Edit Note</MenuItem>}
        {!isClient && canWrite && <MenuItem onClick={() => { setDeleteRow(menuRow); closeMenu(); }} sx={{ color: 'error.main' }}>Delete</MenuItem>}
        {!isClient && <MenuItem onClick={() => { setHistoryRow(menuRow); closeMenu(); }}>View History</MenuItem>}
      </Menu>

      <EnrolmentFormDialog open={formOpen} editing={editRow} defaultCompanyId={isClient ? null : companyId}
        apiBase={apiBase} hideCompanySelect={isClient}
        onClose={() => setFormOpen(false)} onSaved={fetchEnrolments} />
      {!isClient && <EnrolmentHistoryDialog enrolment={historyRow} onClose={() => setHistoryRow(null)} />}
      {!isClient && <EditNoteDialog enrolment={noteRow} onClose={() => setNoteRow(null)} onSaved={upsertRow} />}
      {!isClient && <DeleteEnrolmentDialog enrolment={deleteRow} onClose={() => setDeleteRow(null)} onDeleted={fetchEnrolments} />}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity="error" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </PageContainer>
  );
}
